import React, { useCallback, useState } from "react";

import {
  ActionButton,
  BottomActionSheet,
  CancelButton,
  HeaderInfo,
  HeaderSubtitle,
  HeaderTitle,
  SheetCard,
  SheetHeader,
} from "../../../../../components/BottomActionSheet";
import {
  buildTradeCommandDisplay,
  partialLockNoteOf,
} from "./tradeCommandDisplay";
import { TradeCommandItemProps } from "./types";
import { KebabButton } from "../../../../../components/KebabButton";
import {
  CardAmountFigure,
  CardDate,
  CardDetails,
  CardHeading,
  CardPrimaryRow,
  CardQuantityRow,
  CardQuantityValue,
  CardRow,
  CorrectionNote,
  LockedChip,
  TypeBadge,
  UserNote,
} from "./styles";

export const TradeCommandCard: React.FC<TradeCommandItemProps> = ({
  command,
  actions,
  investmentName,
  settlementAccountName,
}) => {
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  const {
    dateText,
    typeLabel,
    typeTone,
    positionText,
    holdingText,
    detailsText,
    amountText,
    amountVariant,
    quantityText,
    correctionNote,
    lockReason,
    userNote,
  } = buildTradeCommandDisplay(command, investmentName, settlementAccountName);

  const visibleActions = actions.filter((action) => action.show);
  const hasActions = visibleActions.length > 0;
  const partialLockNote = partialLockNoteOf(actions, correctionNote);
  const hasQuantity = command.quantity !== null;

  const openSheet = useCallback(() => {
    setIsSheetOpen(true);
  }, []);

  const closeSheet = useCallback(() => {
    setIsSheetOpen(false);
  }, []);

  const runAction = useCallback(
    (action: () => void) => {
      closeSheet();
      action();
    },
    [closeSheet],
  );

  return (
    <CardRow>
      <CardHeading>
        <CardDate>{dateText}</CardDate>
        <TypeBadge $tone={typeTone}>{typeLabel}</TypeBadge>
        {hasActions ? (
          <KebabButton
            size="small"
            onClick={openSheet}
            aria-label={`Trade options for ${positionText}`}
            aria-haspopup="dialog"
            data-testid={`trade-command-actions-mobile-${command.commandId}`}
          />
        ) : (
          <LockedChip role="note" title={lockReason} aria-label={lockReason}>
            Locked
          </LockedChip>
        )}
      </CardHeading>
      <CardPrimaryRow>
        <span>{holdingText}</span>
        <CardAmountFigure $variant={amountVariant}>
          {amountText}
        </CardAmountFigure>
      </CardPrimaryRow>
      <CardDetails>{detailsText}</CardDetails>
      {userNote && <UserNote>{userNote}</UserNote>}
      {partialLockNote && <CorrectionNote>{partialLockNote}</CorrectionNote>}
      {!hasActions && <CorrectionNote>{lockReason}</CorrectionNote>}
      {hasQuantity && (
        <CardQuantityRow>
          <span>Quantity</span>
          <CardQuantityValue>{quantityText}</CardQuantityValue>
        </CardQuantityRow>
      )}

      {hasActions && (
        <BottomActionSheet
          isOpen={isSheetOpen}
          onClose={closeSheet}
          ariaLabel="Trade actions"
        >
          <SheetCard>
            <SheetHeader>
              <HeaderInfo>
                <HeaderTitle>
                  {typeLabel} · {positionText}
                </HeaderTitle>
                <HeaderSubtitle>
                  {dateText} · {amountText}
                </HeaderSubtitle>
              </HeaderInfo>
            </SheetHeader>
            {visibleActions.map((action) => (
              <ActionButton
                key={action.id}
                type="button"
                $variant={
                  action.variant === "destructive" ? "destructive" : undefined
                }
                onClick={() => runAction(action.onClick)}
              >
                {action.label}
              </ActionButton>
            ))}
          </SheetCard>
          <SheetCard>
            <CancelButton type="button" onClick={closeSheet}>
              Cancel
            </CancelButton>
          </SheetCard>
        </BottomActionSheet>
      )}
    </CardRow>
  );
};
